// Social Media Sharing Utilities

export interface Blog {
  id: number;
  title: string;
  description: string;
  author?: string;
  image?: string;
  tags?: Array<{name: string; slug?: string; id?: number}> | string[];
}

/**
 * Generate hashtags from tag list
 */
export const generateHashtags = (tags: Array<{name: string}> | string[]): string => {
  if (!tags || tags.length === 0) return '';
  
  const tagNames = tags.map(tag => 
    typeof tag === 'string' ? tag : tag.name
  );
  
  return tagNames
    .map(tag => `#${tag.replace(/[^a-zA-Z0-9]/g, '')}`)
    .join(' ');
};

/**
 * Generate platform-specific share text
 */
export const generateSocialShareText = (blog: Blog): {
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
  whatsapp: string;
} => {
  const url = `${window.location.origin}/?page=blogDetail&id=${blog.id}`;
  const hashtags = generateHashtags(blog.tags || []);
  
  // Clean description (remove HTML and image placeholders)
  const cleanDescription = blog.description
    .replace(/\[IMAGE:[^\]]+\]/g, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  return {
    facebook: `${blog.title}\n\n${cleanDescription.substring(0, 300)}...\n\n${hashtags}\n\n${url}`,
    
    twitter: `${blog.title}\n\n${hashtags}\n\n${url}`.substring(0, 280),
    
    instagram: `${blog.title}\n\n${cleanDescription.substring(0, 200)}...\n\n${hashtags}\n\nLink in bio! 🔗`,
    
    linkedin: `${blog.title}\n\n${cleanDescription.substring(0, 400)}...\n\n${hashtags}\n\n${url}`,
    
    whatsapp: `*${blog.title}*\n\n${cleanDescription.substring(0, 200)}...\n\n${hashtags}\n\n${url}`
  };
};

/**
 * Share to social media platform
 */
export const shareToSocialMedia = (platform: string, blog: Blog) => {
  const shareContent = generateSocialShareText(blog);
  const encodedText = encodeURIComponent(shareContent[platform as keyof typeof shareContent]);
  const encodedUrl = encodeURIComponent(`${window.location.origin}/?page=blogDetail&id=${blog.id}`);
  const encodedTitle = encodeURIComponent(blog.title);
  
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}&media=${encodeURIComponent(blog.image || '')}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`
  };
  
  if (shareUrls[platform as keyof typeof shareUrls]) {
    window.open(
      shareUrls[platform as keyof typeof shareUrls], 
      '_blank', 
      'width=600,height=500,scrollbars=yes,resizable=yes'
    );
  } else {
    // Fallback: copy to clipboard
    navigator.clipboard.writeText(shareContent[platform as keyof typeof shareContent])
      .then(() => alert('Share text copied to clipboard!'))
      .catch(() => alert('Failed to copy share text'));
  }
};

/**
 * Copy hashtags to clipboard
 */
export const copyHashtagsToClipboard = (tags: Array<{name: string}> | string[]): Promise<void> => {
  const hashtags = generateHashtags(tags);
  return navigator.clipboard.writeText(hashtags)
    .then(() => console.log('✅ Hashtags copied to clipboard:', hashtags))
    .catch(err => console.error('❌ Failed to copy hashtags:', err));
};

/**
 * Get platform-specific meta tags for SEO
 */
export const generateSocialMetaTags = (blog: Blog): {
  openGraph: Array<{property: string; content: string}>;
  twitter: Array<{name: string; content: string}>;
} => {
  const url = `${window.location.origin}/?page=blogDetail&id=${blog.id}`;
  const imageUrl = blog.image ? `${window.location.origin}/images/${blog.image}` : '';
  
  // Clean description
  const cleanDescription = blog.description
    .replace(/\[IMAGE:[^\]]+\]/g, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 200);
  
  // Extract tag names
  const tagNames = (blog.tags || []).map(tag => 
    typeof tag === 'string' ? tag : tag.name
  );
  
  return {
    openGraph: [
      { property: 'og:title', content: blog.title },
      { property: 'og:description', content: cleanDescription },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'article' },
      { property: 'og:image', content: imageUrl },
      { property: 'og:site_name', content: 'MathKnots' },
      ...tagNames.map(tag => ({ property: 'og:article:tag', content: tag }))
    ],
    twitter: [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: blog.title },
      { name: 'twitter:description', content: cleanDescription },
      { name: 'twitter:image', content: imageUrl },
      { name: 'twitter:site', content: '@MathKnots' }
    ]
  };
};

export default {
  generateHashtags,
  generateSocialShareText,
  shareToSocialMedia,
  copyHashtagsToClipboard,
  generateSocialMetaTags
};




